import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../contexts/ContentContext';
import { getExpertAnchorId } from '../utils/slug';
import { getMediaUrl } from '../utils/mediaUrl';
import { extractYouTubeVideoId, generateYouTubeThumbnail } from '../utils/youtubeUtils';

const Experts = () => {
  const { getContent } = useContent();
  const pageHeader = getContent('experts.pageHeader') || {};
  const team = getContent('experts.team') || {};
  const expertsCta = getContent('experts.cta') || {};
  
  const trainers = team.trainers || [];

  useEffect(() => {
    // Handle hash navigation on page load
    if (window.location.hash) {
      setTimeout(() => {
        const element = document.getElementById(window.location.hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  return (
    <div className="experts-page">
      {/* Page Header */}
      <section className="page-header" style={{ position: 'relative', overflow: 'hidden' }}>
        {/* Hero Image Layer */}
        {pageHeader.heroImage && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            zIndex: 0
          }}>
            <img
              src={getMediaUrl(pageHeader.heroImage)}
              alt="Hero"
              style={pageHeader.heroImageSettings ? {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(calc(-50% + ${pageHeader.heroImageSettings.position.x}px), calc(-50% + ${pageHeader.heroImageSettings.position.y}px)) scale(${pageHeader.heroImageSettings.scale})`,
                transformOrigin: 'center center',
                width: 'auto',
                height: 'auto',
                minWidth: '100%',
                minHeight: '100%',
                maxWidth: 'none',
                objectFit: 'cover'
              } : {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
        )}
        
        {/* Gradient Overlay - Only if enabled */}
        {pageHeader.overlayEnabled !== false && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: pageHeader.overlayColor || '#58c4dc',
            opacity: pageHeader.overlayOpacity ?? 0.85,
            zIndex: 1
          }}></div>
        )}
        
        {/* Content */}
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="page-header-content">
            <h1>{pageHeader.title || 'Notre Équipe d\'Experts'}</h1>
            <p>{pageHeader.subtitle || 'Rencontrez les formateurs de renommée internationale qui transforment l\'avenir de la dentisterie'}</p>
          </div>
        </div>
      </section>

      {/* Nos Formateurs */}
      <section className="expert-team" id="expert-team">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{team.title || 'Nos Formateurs Experts'}</h2>
            <p className="section-subtitle">
              {team.subtitle || 'Rencontrez notre équipe de formateurs qualifiés qui transforment votre pratique dentaire'}
            </p>
          </div>
          
          <div className="trainers-grid">
            {trainers.map((trainer, index) => {
              const hasCv = !!(
                trainer.cv?.experience?.trim() ||
                (trainer.cv?.certifications || []).some((c) => c && String(c).trim()) ||
                (trainer.cv?.achievements || []).some((a) => a && String(a).trim())
              );
              const hasFormations = (trainer.formations || []).some((f) => f && String(f).trim());

              return (
              <div
                key={trainer.id || trainer.slug || index}
                id={getExpertAnchorId(trainer, index)}
                className="trainer-card"
              >
                {/* Top Section: Photo + Name/Title + Specialties filling space */}
                <div className="trainer-top-section">
                  <div className="trainer-header">
                    <div className="trainer-photo">
                      {trainer.photo ? (
                        <div style={{
                          position: 'relative',
                          width: '100%',
                          paddingBottom: trainer.photoSettings?.aspectRatio
                            ? `${(1 / (() => {
                                const ratios = {
                                  '1:1': 1, '4:3': 4 / 3, '16:9': 16 / 9, '4:5': 4 / 5, '3:2': 3 / 2, '21:9': 21 / 9
                                };
                                return ratios[trainer.photoSettings.aspectRatio] || 1;
                              })()) * 100}%`
                            : '100%',
                          overflow: 'hidden'
                        }}>
                          <img 
                            src={getMediaUrl(trainer.photo)} 
                            alt={trainer.name}
                            style={trainer.photoSettings ? {
                              transform: `translate(calc(-50% + ${trainer.photoSettings.position.x}px), calc(-50% + ${trainer.photoSettings.position.y}px)) scale(${trainer.photoSettings.scale})`,
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              minWidth: '100%',
                              minHeight: '100%',
                              width: 'auto',
                              height: 'auto',
                              objectFit: trainer.photoSettings.objectFit || 'cover'
                            } : {
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </div>
                      ) : (
                        <div className="trainer-photo-placeholder">
                          <i className="fas fa-user-md"></i>
                        </div>
                      )}
                    </div>
                    
                    <div className="trainer-info">
                      <h3 className="trainer-name">{trainer.name}</h3>
                      {(trainer.country || trainer.countryFlag || trainer.flagIcon) && (
                        <span className="trainer-country">
                          {(trainer.countryFlag || trainer.flagIcon) && (
                            <span className="country-flag" aria-hidden="true">
                              {trainer.countryFlag || trainer.flagIcon}
                            </span>
                          )}
                          {trainer.country && <span className="country-name">{trainer.country}</span>}
                        </span>
                      )}
                      <p className="trainer-title">{trainer.title}</p>
                    </div>
                  </div>
                  
                  <div className="trainer-specialties">
                    {(trainer.specialties || []).map((specialty, specialtyIndex) => (
                      <span key={specialtyIndex} className="specialty-tag">{specialty}</span>
                    ))}
                  </div>
                </div>
                
                {/* Bottom Section: CV — only if CMS has real data (not old template) */}
                {hasCv && (
                <div className="trainer-cv">
                  <h4>Curriculum Vitae</h4>
                  <div className="cv-section">
                    {trainer.cv?.experience?.trim() && (
                    <div className="cv-item">
                      <strong>Expérience:</strong>
                      <span>{trainer.cv.experience}</span>
              </div>
                    )}
                    {(trainer.cv?.certifications || []).length > 0 && (
                    <div className="cv-item">
                      <strong>Certifications:</strong>
                      <ul>
                        {(trainer.cv?.certifications || []).map((cert, certIndex) => (
                          <li key={certIndex}>{cert}</li>
                        ))}
                      </ul>
                  </div>
                    )}
                    {(trainer.cv?.achievements || []).length > 0 && (
                    <div className="cv-item">
                      <strong>Réalisations:</strong>
                      <ul>
                        {(trainer.cv?.achievements || []).map((achievement, achIndex) => (
                          <li key={achIndex}>{achievement}</li>
                        ))}
                      </ul>
                </div>
                    )}
              </div>
            </div>
                )}

                {hasFormations && (
                <div className="trainer-formations">
                  <h4>Formations Animées</h4>
                  <div className="formations-list">
                    {(trainer.formations || []).map((formation, formIndex) => (
                      <div key={formIndex} className="formation-item">
                        <i className="fas fa-graduation-cap"></i>
                        <span>{formation}</span>
                </div>
                    ))}
                  </div>
                </div>
                )}

                {(trainer.presentationVideos || []).length > 0 && (
                  <div className="trainer-presentation-videos">
                    <h4>Vidéos de Présentation</h4>
                    <div className="presentation-videos-grid">
                      {(trainer.presentationVideos || []).map((video, vidIndex) => {
                        const youtubeId = video.youtube_id || extractYouTubeVideoId(video.youtube_url);
                        if (!youtubeId) return null;
                        const watchUrl = video.youtube_url || `https://www.youtube.com/watch?v=${youtubeId}`;
                        const thumbnail = video.thumbnail || video.video_thumbnail || generateYouTubeThumbnail(youtubeId);

                        return (
                          <div key={vidIndex} className="presentation-video-item">
                            <div
                              className="youtube-video-container"
                              onClick={() => window.open(watchUrl, '_blank')}
                              style={{
                                position: 'relative',
                                cursor: 'pointer',
                                borderRadius: '8px',
                                overflow: 'hidden'
                              }}
                            >
                              <img
                                src={thumbnail}
                                alt={video.title || `Vidéo de présentation ${vidIndex + 1}`}
                                style={{ width: '100%', height: '180px', objectFit: 'cover' }}
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiB2aWV3Qm94PSIwIDAgMTIwIDkwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00OCA0MEw3MiA1NUw0OCA3MFY0MFoiIGZpbGw9IiM2QjcyODAiLz4KPC9zdmc+';
                                }}
                              />
                              <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: '#ff0000',
                                fontSize: '3rem',
                                textShadow: '0 2px 8px rgba(0,0,0,0.8)'
                              }}>
                                <i className="fab fa-youtube"></i>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>{expertsCta.title || 'Prêt à Transformer Votre Pratique ?'}</h2>
            <p>{expertsCta.subtitle || 'Rejoignez plus de 500 dentistes qui ont fait confiance à DentoXcellence pour leur développement professionnel'}</p>
            <div className="cta-buttons">
              {(expertsCta.buttons || []).map((button, index) => (
                button.link?.startsWith('http') ? (
                  <a key={index}
                     href={button.link} 
                     className={`btn btn-${button.type}`}
                     target="_blank" 
                     rel="noopener noreferrer">
                    <i className={button.icon}></i>
                    {button.text}
                  </a>
                ) : (
                  <Link key={index} to={button.link} className={`btn btn-${button.type}`}>
                    <i className={button.icon}></i>
                    {button.text}
                  </Link>
                )
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Experts;


